import '../../themes/confirm_hyg.scss';
import '../../themes/global.scss';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router';
import { Column, Grid, Row, Screen } from '@tempmee/react';
import { BackButton, Box } from '~/commonComponents';
import { BestInDentalProfessionalPopup } from '~/commonComponents/popups/BestInDentalProfessionalPopup';
import { PreApprovedChargeTimelineBanner } from '~/components/ConfirmCandidate/components/PreApprovedChargeTimelineBanner';
import browserHistory from '~/browserHistory';
import { selectOffice } from '../../actions';
import { CenteredSpinner } from '../../commonComponents';
import { BestInDentalStatus } from '../../enums/BestInDental';
import { getShouldShowBackgroundCheckBadge } from '../../growthbook';
import { BackgroundCheckStatusEnum } from '../../models';
import { shades, shadows } from '@tempmee/tailwind';
import { ProfessionalBestInDentalWinnerTooltip } from '../BestInDental';
import { PaymentWarningBanner } from '../PaymentWarningBanner';
import { CandidateBadges } from '../candidates/CandidateBadges';
import { CandidateHeader } from './components/CandidateHeader';
import { CandidateSelection } from './components/CandidateSelection/CandidateSelection';
import { CollapsibleCandidateHeader } from './components/CandidateSelection/CollapsibleCandidateHeader';
import { CollapsibleCandidateSelection } from './components/CandidateSelection/CollapsibleCandidateSelection';
import { ChargeTimelineCollapsibleBanner } from './components/ChargeTimelineCollapsibleBanner';
import { ConfirmCandidateAnotherJobPopup } from './components/ConfirmCandidateAnotherJobPopup';
import { ConfirmCandidateEligibleJobsPopup } from './components/ConfirmCandidateEligibleJobsPopup';
import { ConfirmExitPopup } from './components/ConfirmExitPopup';
import { DeclineSuccessPopups } from './components/DeclineSuccessPopups';
import { NotesForProfessional } from './components/NotesForProfessional';
import { PaymentMissingPopup } from './components/PaymentMissingPopup';
import { ShiftDetails } from './components/ShiftDetails';
import { WarningBanner } from './components/WarningBanner';
import {
  fireConfirmCandidateCandidateViewed,
  fireConfirmCandidateLeaveClicked,
} from '~/posthogs/events';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import { EnablePermanentJobM2M } from '~/constants/ff.posthog';
import { ExperienceOverview } from '../CandidateOverview/containers';
import { VerifiedCredentials } from '~/components/ProfessionalProfile/components/ProfileCard/components/VerifiedCredentials';
import { useConfirmCandidateData } from './hooks/useConfirmCandidateData';
import { useScrollCollapse } from './hooks/useScrollCollapse';
import { useConfirmExitNavigation } from './hooks/useConfirmExitNavigation';
import { usePaymentsForJob } from './hooks/usePaymentsForJob';
import { useCandidateUrlSync } from './hooks/useCandidateUrlSync';
import { useConfirmOutreachSource } from './hooks/useConfirmOutreachSource';
import { getCandidatePosition, getConfirmScreenStatus, getDeclineSuccessRoute } from './utils';

export const ConfirmCandidate = ({ isSingleView = false }) => {
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const dispatch = useDispatch();
  const jobId = location.state?.jobId || params.jobId;
  const candidateId = params?.candidateId;
  const user = useSelector((state) => state.user.selectedChildOffice ?? state.user.user);
  const [notesForTemp, setNotesForTemp] = useState(user.notes_for_temp || '');

  const {
    jobQuery,
    job,
    hasLoadedJob,
    hasCorrectJob,
    isOnsiteInterview,
    candidate,
    currentCounter,
    allCandidates,
    visibleCandidates,
    loadingCandidates,
    isSuccessFetchJobCandidates,
    reloadCandidates,
    showCandidatePage,
    displayProfession,
    authorizations,
    isCredentialV2,
  } = useConfirmCandidateData({ jobId, candidateId });

  // Attributes a confirmation-outreach email landing (GOTU-4906). This is the one
  // entry point that lands directly on this screen, so there is no earlier click to
  // record it; the applied/counter split comes from the shift's status.
  useConfirmOutreachSource({ jobId: job?.id, jobStatus: job?.status });

  const isEnablePermanentJobM2M = useFeatureFlag(EnablePermanentJobM2M);

  const paymentMethodsQuery = usePaymentsForJob({
    job,
    jobId,
    hasLoadedJob,
    allCandidates,
    visibleCandidates,
  });
  const isPaymentLoading = paymentMethodsQuery.isLoading || paymentMethodsQuery.isFetching;
  // Gates the cold-entry SCHEDULED redirect while a confirm is in flight — see
  // getConfirmScreenStatus (GOTU-4020/4025/4027) for the full rationale.
  const isError = paymentMethodsQuery.isError;

  const [isConfirmJobLoading, setIsConfirmJobLoading] = useState(false);
  const [isDeclineCandidateLoading, setIsDeclineCandidateLoading] = useState(false);
  const [isDeclineCandidateSuccess, setIsDeclineCandidateSuccess] = useState(false);
  // Holds the empty-list redirect off once a confirm lands — see getConfirmScreenStatus.
  const [hasConfirmedCandidate, setHasConfirmedCandidate] = useState(false);
  const [isShowPaymentMissingPopup, setIsShowPaymentMissingPopup] = useState(false);
  const isLoading = isPaymentLoading || isConfirmJobLoading || isDeclineCandidateLoading;
  const [confirmCandidateAnotherJobPopup, setConfirmCandidateAnotherJobPopup] = useState({
    show: false,
    candidate: null,
    officeId: null,
    hasPendingJobs: false,
    shiftId: null,
    offerId: null,
  });
  const [confirmCandidateEligibleJobsPopup, setConfirmCandidateEligibleJobsPopup] = useState({
    show: false,
    candidate: null,
    professionalId: null,
    officeId: null,
    job: null,
  });

  const hasCandidatesAvailable = allCandidates?.length > 0;
  const hasMultipleCounters = allCandidates?.length > 1;

  const candidateRef = useRef(candidate);
  const hasMultipleCountersRef = useRef(false);
  // The SCHEDULED-job redirect is only a cold-entry fallback for stale confirm URLs.
  // Once the confirm workflow has rendered, navigation should be driven by the
  // confirm outcome/popup instead of inferred from a refetched job status.
  const hasRenderedConfirmWorkflowRef = useRef(false);

  const isConfirmWorkflowReady =
    !isLoading &&
    !jobQuery.isLoading &&
    !loadingCandidates &&
    hasCorrectJob &&
    Boolean(job?.best_counter) &&
    Boolean(candidate) &&
    !(isDeclineCandidateSuccess && isOnsiteInterview);

  const isBestInDentalWinner = useMemo(() => {
    return candidate?.professional_info?.bestInDental?.status === BestInDentalStatus.Winner;
  }, [candidate]);

  const {
    showCollapsibleSelection,
    showCollapsibleHeader,
    candidateSelectionRef,
    candidateHeaderRef,
  } = useScrollCollapse({ isSingleView, hasMultipleCounters });

  const {
    allowNavigation,
    clearPendingNavigation,
    navigateToPending,
    handleNavigateWithoutBlocking,
    confirmationExitModalOpen,
    setConfirmationExitModalOpen,
  } = useConfirmExitNavigation({ jobId, isError, hasCandidatesAvailable });

  useCandidateUrlSync({
    jobId,
    candidateId,
    allCandidates,
    visibleCandidates,
    isSuccessFetchJobCandidates,
    reloadCandidates,
    showCandidatePage,
  });

  const handleDeclineConfirmed = useCallback((hasMultiple) => {
    hasMultipleCountersRef.current = hasMultiple;
  }, []);

  const closeConfirmCandidateAnotherJobPopup = useCallback(() => {
    setConfirmCandidateAnotherJobPopup({
      show: false,
      candidate: null,
      officeId: null,
      hasPendingJobs: false,
      shiftId: null,
      offerId: null,
    });
  }, []);

  const closeConfirmCandidateEligibleJobsPopup = useCallback(() => {
    setConfirmCandidateEligibleJobsPopup({
      show: false,
      candidate: null,
      professionalId: null,
      officeId: null,
      job: null,
    });
  }, []);

  const handleConfirmOutcome = useCallback(({ outcome, response }, variables) => {
    setHasConfirmedCandidate(true);

    if (outcome.type === 'show_another_job_popup') {
      setConfirmCandidateAnotherJobPopup({
        show: true,
        candidate: variables.candidate,
        officeId: variables.officeId,
        hasPendingJobs: outcome.hasPendingJobs,
        // Taken from the confirm variables rather than read off current state at
        // popup time: the confirm invalidates the job query, so currentCounter can
        // have moved on by then. These are the shift/offer that were confirmed.
        shiftId: variables.jobId,
        offerId: variables.counterOfferId,
      });
      return;
    }

    if (outcome.type === 'show_eligible_jobs_popup') {
      setConfirmCandidateEligibleJobsPopup({
        show: true,
        candidate: variables.candidate,
        professionalId: variables.candidate?.id,
        officeId: variables.officeId,
        job: response,
      });
    }
  }, []);

  const handleDeclineSuccess = useCallback(() => {
    setIsDeclineCandidateSuccess(true);
  }, []);

  const handlePaymentMissing = useCallback(() => {
    setIsShowPaymentMissingPopup(true);
  }, []);

  useEffect(() => {
    if (candidate) {
      candidateRef.current = candidate;
    }
  }, [candidate]);

  // Fire candidate_viewed once per distinct candidate view — including the
  // default candidate on open — as soon as both the candidate and the counter
  // list are ready. The ref dedups so a re-render or list refetch never
  // re-fires for the same candidate, and this is the single source (the
  // candidate tabs only navigate, letting this effect fire on the route change).
  const lastViewedCandidateIdRef = useRef(null);

  // The exact position reported by the last candidate_viewed, kept so the
  // confirm/decline actions can report what the office actually saw rather than
  // re-deriving it later (GOTU-4816).
  const [viewedPosition, setViewedPosition] = useState(null);

  useEffect(() => {
    if (!candidate?.id || !allCandidates?.length || !jobId) return;
    if (lastViewedCandidateIdRef.current === candidate.id) return;

    lastViewedCandidateIdRef.current = candidate.id;

    const position = getCandidatePosition(allCandidates, candidate.id);

    // Frozen here, not recomputed at click time. Declining a candidate
    // invalidates the counter list, so by the time the office confirms the
    // survivor the list has shrunk — recomputing then reported her as "1 of 1"
    // when her own impression, moments earlier, said "2 of 2". That both breaks
    // the impression↔outcome join and inverts the question this data exists to
    // answer: an office that declined the first pro and picked the second would
    // look like it took the only one available.
    setViewedPosition({ candidateId: candidate.id, position });

    fireConfirmCandidateCandidateViewed({
      shiftId: jobId,
      offerId: currentCounter?.id,
      professionalId: candidate.id,
      ...position,
    });
  }, [candidate?.id, jobId, allCandidates, currentCounter?.id]);

  // Fires on "Leave Anyway" from the exit popup, before the deferred navigation
  // runs. Position is reused from the impression when available (same rationale
  // as the decline event) so the abandonment joins to what the office saw.
  const handleLeaveAnyway = useCallback(() => {
    const positionAtImpression =
      viewedPosition?.candidateId === candidate?.id
        ? viewedPosition.position
        : getCandidatePosition(allCandidates, candidate?.id);

    fireConfirmCandidateLeaveClicked({
      shiftId: jobId,
      professionalId: candidate?.id,
      offerId: currentCounter?.id,
      ...positionAtImpression,
    });

    navigateToPending();
  }, [viewedPosition, candidate?.id, allCandidates, jobId, currentCounter?.id, navigateToPending]);

  useEffect(() => {
    if (isConfirmWorkflowReady) {
      hasRenderedConfirmWorkflowRef.current = true;
    }
  }, [isConfirmWorkflowReady]);

  const handleBackButton = () => {
    if (location.state?.fromBulkConfirmation) {
      navigate(-1);
    } else {
      navigate('/dashboard/jobs/pending');
    }
  };

  const handleDeclineSuccessClose = () => {
    allowNavigation();
    navigate(
      getDeclineSuccessRoute({
        isEnablePermanentJobM2M,
        hasMultipleCounters: hasMultipleCountersRef.current,
        jobId,
        postingId: job?.postingId,
      }),
      { replace: true },
    );
    setIsDeclineCandidateSuccess(false);
  };

  const showConfirmCandidateAnotherJobPopup = confirmCandidateAnotherJobPopup.show;
  const showConfirmCandidateEligibleJobsPopup = confirmCandidateEligibleJobsPopup.show;

  const screenStatus = getConfirmScreenStatus({
    isAnotherJobPopupOpen:
      showConfirmCandidateAnotherJobPopup || showConfirmCandidateEligibleJobsPopup,
    isConfirmWorkflowReady,
    isConfirmJobLoading,
    hasRenderedConfirmWorkflow: hasRenderedConfirmWorkflowRef.current,
    hasConfirmedCandidate,
    job,
    jobId,
    isSuccessFetchJobCandidates,
    hasCandidatesAvailable,
    isOnsiteInterview,
    isDeclineCandidateSuccess,
  });

  // Applied from an effect, not during render: with the exit guard still installed,
  // a render-phase navigate('/') is intercepted by the blocker, which sets this
  // component's own state mid-render and loops React into "Too many re-renders"
  // (GOTU-19091). By now the guard's own cleanup has normally removed the blocker
  // (it only blocks while candidates exist); allowNavigation() is defensive, so an
  // empty shift never asks the office to confirm leaving it.
  useEffect(() => {
    if (screenStatus !== 'redirect_empty') return;
    allowNavigation();
    navigate('/');
  }, [screenStatus, allowNavigation, navigate]);

  // Loading states render inside the same DS Screen as the ready view (below) so the
  // spinner sits on the correctly-sized surface — a bare <CardSpinner/> renders the
  // legacy Container shell, which differs in size and causes a layout flash on entry.
  const loadingScreen = (
    <Screen variant="standard" className="global_font">
      <Screen.Header
        start={<BackButton onClick={handleBackButton} />}
        title="Confirm Candidate"
      />
      <Screen.Body inset>
        <CenteredSpinner minHeight={400} />
      </Screen.Body>
    </Screen>
  );

  if (screenStatus === 'another_job_popup') {
    return (
      <>
        {loadingScreen}
        <ConfirmCandidateAnotherJobPopup
          show={showConfirmCandidateAnotherJobPopup}
          candidate={confirmCandidateAnotherJobPopup.candidate}
          officeId={confirmCandidateAnotherJobPopup.officeId}
          job={job}
          shiftId={confirmCandidateAnotherJobPopup.shiftId}
          offerId={confirmCandidateAnotherJobPopup.offerId}
          hasPendingJobs={confirmCandidateAnotherJobPopup.hasPendingJobs}
          onClose={closeConfirmCandidateAnotherJobPopup}
        />
        <ConfirmCandidateEligibleJobsPopup
          show={showConfirmCandidateEligibleJobsPopup}
          candidate={confirmCandidateEligibleJobsPopup.candidate}
          professionalId={confirmCandidateEligibleJobsPopup.professionalId}
          officeId={confirmCandidateEligibleJobsPopup.officeId}
          job={confirmCandidateEligibleJobsPopup.job}
          onClose={closeConfirmCandidateEligibleJobsPopup}
          navigateWithoutBlocking={handleNavigateWithoutBlocking}
        />
      </>
    );
  }

  if (screenStatus === 'redirect_scheduled') {
    // Render-phase redirect (kept here rather than in an effect) so a stale
    // SCHEDULED URL resolves before commit. Guard rationale lives in
    // getConfirmScreenStatus (GOTU-4020/4025/4027).
    browserHistory.replace(`/dashboard/job/${job.id}/details`);
    return <></>;
  }

  if (screenStatus !== 'ready') {
    // 'loading', 'decline_success' or 'redirect_empty' (left from the effect above)
    return (
      <>
        {loadingScreen}
        {screenStatus === 'decline_success' && (
          // Rendered alongside the spinner because Monolith Decline Candidate forces a full-screen load.
          <DeclineSuccessPopups
            isEnablePermanentJobM2M={isEnablePermanentJobM2M}
            professionalName={`${candidateRef.current?.first_name} ${candidateRef.current?.last_name}`}
            onClose={handleDeclineSuccessClose}
          />
        )}
      </>
    );
  }

  return (
    <>
      <Screen variant="standard" className="global_font">
        <Screen.Header
          start={<BackButton onClick={handleBackButton} />}
          title="Confirm Candidate"
        />
        <Screen.Body inset>
          <PaymentWarningBanner
            officeId={job?.offer_owner?.id}
            containerStyle={{ marginTop: '-10px', marginInline: '-30px' }}
          />

          {!isSingleView && (
            <div ref={candidateSelectionRef} style={{ position: 'relative' }}>
              <div style={{ opacity: showCollapsibleSelection ? 0 : 1 }}>
                <CandidateSelection allCandidates={allCandidates} candidates={visibleCandidates} />
              </div>
              <CollapsibleCandidateSelection
                show={showCollapsibleSelection}
                allCandidates={allCandidates}
                candidates={visibleCandidates}
              />
            </div>
          )}

          <Column
            width="100%"
            justify="center"
            // DS Column has no backgroundColor/boxShadow/margin props, so the card
            // chrome stays inline — but using DS tokens (shades/shadows) instead of
            // the legacy Colors constant + a hardcoded shadow string.
            style={{
              marginTop: -1,
              backgroundColor: shades[0],
              boxShadow: shadows['elevation-4'],
              position: 'relative',
            }}
          >
            <CollapsibleCandidateHeader
              show={showCollapsibleHeader}
              candidate={candidate}
              job={job}
              currentCounter={currentCounter}
              allCandidates={allCandidates}
              viewedPosition={viewedPosition}
              notesForTemp={notesForTemp}
              allowNavigation={allowNavigation}
              onDeclineConfirmed={handleDeclineConfirmed}
              onConfirmOutcome={handleConfirmOutcome}
              onConfirmPendingChange={setIsConfirmJobLoading}
              onDeclinePendingChange={setIsDeclineCandidateLoading}
              onDeclineSuccess={handleDeclineSuccess}
              onPaymentMissing={handlePaymentMissing}
            />

            <div style={{ padding: 20, position: 'relative' }}>
              <div style={{ opacity: showCollapsibleHeader ? 0 : 1 }}>
                <CandidateHeader
                  ref={candidateHeaderRef}
                  candidate={{
                    ...candidate,
                    hasWorkedWithDSO: currentCounter?.hasWorkedWithDSO,
                    hasWorkedWithOffice: currentCounter?.hasWorkedWithOffice,
                  }}
                  isHasBackgroundCheck={
                    currentCounter?.requestor?.backgroundCheck?.status ===
                      BackgroundCheckStatusEnum.Clear && getShouldShowBackgroundCheckBadge()
                  }
                  displayProfession={displayProfession}
                  notesForTemp={notesForTemp}
                  allowNavigation={allowNavigation}
                  onDeclineConfirmed={handleDeclineConfirmed}
                  onConfirmOutcome={handleConfirmOutcome}
                  onConfirmPendingChange={setIsConfirmJobLoading}
                  onDeclinePendingChange={setIsDeclineCandidateLoading}
                  onDeclineSuccess={handleDeclineSuccess}
                  onPaymentMissing={handlePaymentMissing}
                  job={job}
                  currentCounter={currentCounter}
                  allCandidates={allCandidates}
                  viewedPosition={viewedPosition}
                />
              </div>

              <Grid columns="1.25fr 1fr" gap={5} px={5}>
                <Box minWidth={0} wordBreak="break-all" gap={20}>
                  <Column>
                    {isBestInDentalWinner && (
                      <Row justify="end">
                        <ProfessionalBestInDentalWinnerTooltip />
                      </Row>
                    )}
                    <CandidateBadges candidate={candidate} />
                    <WarningBanner candidate={candidate} job={job} />
                  </Column>
                  {isCredentialV2 && <VerifiedCredentials authorizations={authorizations} />}
                  <ExperienceOverview
                    competingOffersCount={currentCounter?.competingOffersCount}
                    hasWorkedWithDSO={currentCounter?.hasWorkedWithDSO}
                    hasWorkedWithOffice={currentCounter?.hasWorkedWithOffice}
                    job={job}
                    professional={candidate}
                  />
                </Box>
                <div>
                  <ShiftDetails currentCounter={currentCounter} job={job} />

                  <ChargeTimelineCollapsibleBanner job={job} />

                  <PreApprovedChargeTimelineBanner job={job} />

                  <NotesForProfessional value={notesForTemp} onChange={setNotesForTemp} />
                </div>
              </Grid>
            </div>
          </Column>
        </Screen.Body>
      </Screen>

      {isShowPaymentMissingPopup ? (
        <PaymentMissingPopup
          onGoBack={() => setIsShowPaymentMissingPopup(false)}
          onAddPayment={() => {
            allowNavigation();
            setIsShowPaymentMissingPopup(false);
            dispatch(
              selectOffice({ officeId: job?.offer_owner?.id, redirectUrl: '/account/payment' }),
            );
          }}
        />
      ) : null}

      {confirmationExitModalOpen && (
        <ConfirmExitPopup
          onGoBack={() => {
            clearPendingNavigation();
            setConfirmationExitModalOpen(false);
          }}
          onLeave={handleLeaveAnyway}
        />
      )}

      <BestInDentalProfessionalPopup candidateId={candidateId} />
    </>
  );
};
